import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UsuarioSignIn } from 'src/app/models/User/UsuarioSignIn.model';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';
import { SessionService } from 'src/app/services/session/session.service';
import { AppConfigService } from 'src/app/services/app-config/app-config.service';

type LoginStep = 'email' | 'password' | 'chooseLogin';

@Component({
  selector: 'app-signIn',
  templateUrl: './signIn.page.html',
  styleUrls: ['./signIn.page.scss'],
  providers: []
})
export class SignInPage {
  public hide: boolean = true;
  emailFormGroup: FormGroup;
  passwordFormGroup: FormGroup;
  public step: LoginStep = 'email';
  public hasMultipleUsers: boolean = false;
  public loading: boolean = false;
  public accountInactive: boolean = false;
  public inactiveMessage: string = "";
  public emailNotFound: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private messageService: InvalidFieldMessageService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private sessionService: SessionService,
    public appConfig: AppConfigService) {
    this.authenticationService.verifyIfIsLogged();
    this.emailFormGroup = this.formBuilder.group({
      email: ['', [Validators.required, Validators.maxLength(100), Validators.minLength(5), Validators.email]]
    });
    this.passwordFormGroup = this.formBuilder.group({
      user: [''],
      password: ['', [Validators.required, Validators.maxLength(30), Validators.minLength(8)]],
      authMode: ['admin']
    });

    this.passwordFormGroup.get('password')?.markAsUntouched();

    this.passwordFormGroup.get('authMode')?.valueChanges.subscribe((authMode) => {
      const passwordCtrl = this.passwordFormGroup.get('password');
      const userCtrl = this.passwordFormGroup.get('user');

      if (passwordCtrl) {
        passwordCtrl.reset();
        passwordCtrl.markAsUntouched();
        passwordCtrl.markAsPristine();
      }

      if (userCtrl) {
        userCtrl.reset();
        userCtrl.markAsUntouched();
        userCtrl.markAsPristine();
      }
    });
  }

  get message() {
    return this.messageService
  }

  get form() {
    return this.emailFormGroup.controls;
  }

  get passwordForm() {
    return this.passwordFormGroup.controls;
  }

  public errorMessageEmail(): string {
    return ''
  }

  public get email(): string {
    return this.emailFormGroup.controls['email'].value;
  }

  public async advance() {
    this.emailNotFound = false;
    this.accountInactive = false;
    if (this.emailFormGroup.invalid) {
      return;
    }

    this.loading = true;
    try {
      const r = await this.authenticationService.getAccountLoginInfo(this.email);
      if (!r.success) {
        throw (r);
      }

      if (!r.data.exists) {
        this.router.navigate(['/sign-up'], { queryParams: { email: this.email } });
        return;
      }

      this.hasMultipleUsers = r.data.hasMultipleUsers;
      this.step = this.hasMultipleUsers ? 'chooseLogin' : 'password';
      if (this.hasMultipleUsers) {
        this.passwordFormGroup.get('authMode')?.setValue('admin');
        this.passwordFormGroup.get('password')?.markAsUntouched();
        this.passwordFormGroup.get('password')?.markAsPristine();
      }
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  public backToEmail() {
    this.step = 'email';
    this.accountInactive = false;
    this.emailNotFound = false;
    this.passwordFormGroup.reset();
  }

  private createUsuarioSignIn(): UsuarioSignIn {
    const authMode = this.passwordForm['authMode'].value;
    const asAdministrator = authMode === 'admin' || this.step === 'password';
    return {
      email: this.email,
      password: this.passwordForm['password'].value,
      user: asAdministrator ? undefined : (this.passwordForm['user'].value || '').trim().toLowerCase() || undefined,
      asAdministrator: asAdministrator
    };
  }

  public async enter() {
    if (this.passwordForm['password'].invalid ||
        (this.step === 'chooseLogin' && this.passwordForm['authMode'].value === 'user' && !this.passwordForm['user'].value)) {
      return;
    }
    await this.login(this.createUsuarioSignIn());
  }

  private async login(data: UsuarioSignIn) {
    this.loading = true;
    try {
      const r = await this.authenticationService.signIn(data);
      if (r.success) {
        if (this.sessionService.getDetails().wizardFinished)
          this.router.navigate(['/home']);
        else
          this.router.navigate(['/wizard']);
        return;
      }

      throw (r);
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  private handleError(err: any) {
    const messageCode = (err as any).messageCode;

    // 403: usuário inativo. 401 cobre tanto credenciais inválidas quanto conta/empresa
    // inativa - o backend não distingue os dois casos por código, só pela mensagem,
    // por isso usamos a mensagem do backend (com fallback traduzido) em vez de um
    // texto genérico fixo, para não esconder o motivo real (ex: "conta inativa").
    if (messageCode == 403) {
      this.accountInactive = true;
      this.inactiveMessage = (err as any).message || this.translateService.instant("signIn.invalidLogin");
      return;
    }

    if (messageCode == 401) {
      this.snackBar.open((err as any).message || this.translateService.instant("signIn.invalidLogin"), '', {
        duration: 10000,
        verticalPosition: 'top',
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (messageCode == 429) {
      this.snackBar.open((err as any).message || this.translateService.instant("signIn.tooManyRequests"), '', {
        duration: 10000,
        verticalPosition: 'top',
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.snackBar.open((err as any).message, '', {
      duration: 10000,
      verticalPosition: 'top',
      panelClass: ['red-snackbar']
    });
  }
}
